import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, faUsers, faClock, faPlay, 
  faCheckCircle, faDatabase, faServer, 
  faCloud, faCode, faProjectDiagram,
  faLaptopCode, faCertificate, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import './HeroSection.css';

const CloudComputing = () => {
  const [showModal, setShowModal] = useState(false);
  const [formCourseName, setFormCourseName] = useState('Cloud Computing');
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
  });

  const openModal = () => setShowModal(true);
  const closeModal = useCallback(() => {
    setShowModal(false);
    setFormData({ fullName: '', mobileNumber: '', email: '' });
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobileNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { fullName, mobileNumber, email } = formData;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (!fullName || !emailRegex.test(email) || mobileNumber.length !== 10) {
      alert('Please fill in valid details.');
      return;
    }

    try {
      const response = await fetch('https://api.dikshasea.in/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, course: formCourseName }),
      });

      const result = await response.json();
      if (response.ok) {
        alert('Enrollment successful!');
        closeModal();
      } else {
        alert(result.message || 'Submission failed');
      }
    } catch (err) {
      console.error(err);
      alert('Error submitting form');
    }
  };

  return (
    <>
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="badge bg-primary bg-opacity-10 text-white mb-3">New Cloud Course</span>
              <h1 className="hero-title">
                Master <span className="hero-highlight">Cloud Computing</span>
              </h1>
              <p className="hero-subtitle">
                Transform your career with our comprehensive cloud computing program. 
                Gain expertise in AWS, Azure, and Google Cloud platforms through hands-on projects 
                and real-world scenarios.
              </p>
              
              <div className="course-meta">
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <div className="fw-bold">4.8/5.0</div>
                    <small className="text-muted">(3,200 reviews)</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <div className="fw-bold">12,500+</div>
                    <small className="text-muted">Students</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="fw-bold">60 hours</div>
                    <small className="text-muted">Content</small>
                  </div>
                </div>
              </div>
              
              <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ratio ratio-16x9">
                <img 
                  src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
                  alt="Cloud Computing Course" 
                  className="img-fluid rounded-3 shadow"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="course-details">
          <div className="course-content">
            {/* About Course */}
            <section className="about-course">
              <h2 className="section-title">About This Course</h2>
              <p>This comprehensive Cloud Computing program will take you from fundamental concepts to advanced cloud architecture. You'll learn to design, deploy, and manage applications across AWS, Azure, and Google Cloud platforms.</p>
              <p>Through real-world projects, you'll gain experience with cloud-native technologies, serverless computing, containerization, and infrastructure as code.</p>
            </section>
            
            {/* Curriculum */}
            <section className="curriculum">
              <h2 className="section-title">Curriculum</h2>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCloud} className="me-2" /> Module 1: Cloud Fundamentals</h3>
                <p>Master the core concepts and architecture of cloud computing</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Cloud service models (IaaS, PaaS, SaaS)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Cloud deployment models</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Virtualization and container basics</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Cloud security fundamentals</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faServer} className="me-2" /> Module 2: AWS Cloud Practitioner</h3>
                <p>Become proficient in Amazon Web Services platform</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> EC2, S3, and VPC fundamentals</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> AWS IAM and security</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Serverless with Lambda</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> AWS pricing and billing</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faDatabase} className="me-2" /> Module 3: Azure Cloud Services</h3>
                <p>Develop expertise in Microsoft's cloud platform</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Azure Virtual Machines</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Azure Storage Solutions</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Azure Active Directory</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Azure Kubernetes Service</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCode} className="me-2" /> Module 4: Google Cloud Platform</h3>
                <p>Master Google's cloud infrastructure and services</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Compute Engine and App Engine</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> BigQuery and Dataflow</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Cloud Functions</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> GCP security and operations</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faProjectDiagram} className="me-2" /> Module 5: Cloud Architecture</h3>
                <p>Design and implement enterprise-grade cloud solutions</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Multi-cloud strategies</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Infrastructure as Code (Terraform)</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> CI/CD in the cloud</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Disaster recovery planning</div>
                </div>
              </div>
            </section>
          </div>  
          
          <div className="course-sidebar">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="info-content">
                <h4>Duration</h4>
                <p>16 Weeks (15 hrs/week)</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <div className="info-content">
                <h4>Level</h4>
                <p>Beginner to Advanced</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <div className="info-content">
                <h4>Projects</h4>
                <p>6 Real-world Projects</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="info-content">
                <h4>Prerequisites</h4>
                <p>Basic networking, Linux fundamentals</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="info-content">
                <h4>Certifications</h4>
                <p>AWS, Azure, GCP prep</p>
              </div>
            </div>
            
            <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
          </div>
        </div>
      </div>
      {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">{formCourseName}</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-outline-dark me-2" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Submit</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CloudComputing;